import { PureComponent } from 'react'
import { connectLoadable } from 'helpers/loadableHelpers'
import { recaptchaGuard } from 'guards/recaptchaGuards'
import { WrappedFieldInputProps, WrappedFieldMetaProps } from 'redux-form'
import * as debounce from 'lodash/debounce'
import 'styles/atoms/Recaptcha.css'
import { Guards } from 'interfaces/guards'
import {
  registerRecaptchaWidgetActionCreator,
  unregisterRecaptchaWidgetActionCreator,
  recaptchaFailedActionCreator,
  RegisterRecaptchaWidgetPayload,
} from 'actions/recaptchaActions'
import { RecaptchaPlacement, RecaptchaWidgetID } from 'interfaces/recaptcha'
import { RecaptchaWindow } from 'interfaces/DOM'
import { RECAPTCHA_KEY } from 'constants/api'
import { captureWarning } from 'helpers/errorHelpers'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'
import { recaptchaIsFailedToLoadSelector } from 'selectors/recaptchaSelectors'
import * as styles from 'styles/molecules/forms/Form.css'
import { SUPPORT_EMAIL } from 'constants/email'
import { StoreDispatch } from 'interfaces/component'
import { IS_STATUS_APP } from 'helpers/userAgentHelpers'
import { Link } from 'react-router-dom'
import { EmailLink } from 'components/features/pfm/EmailLink/EmailLink'

declare const window: RecaptchaWindow

export type RecaptchaOwnProps = {
  placement: RecaptchaPlacement
  input: WrappedFieldInputProps & {
    onChange: (token: string) => void
  }
  meta: WrappedFieldMetaProps<any>
  onResponse: () => void
}

type RecaptchaDispatchProps = {
  registerRecaptchaWidget: typeof registerRecaptchaWidgetActionCreator
  unregisterRecaptchaWidget: () => void
  recaptchaFailed: () => void
}

type RecaptchaProps = RecaptchaDispatchProps & RecaptchaOwnProps

class RecaptchaComponent extends PureComponent<RecaptchaProps> {
  private elementID = `recaptcha_${ this.props.placement }`
  private widgetID: RecaptchaWidgetID
  private overlayNode: HTMLDivElement | null = null

  render () {
    return (
      <div>
        <div id={ this.elementID } />
        { this.renderRecaptchaOverlay() }
      </div>
    )
  }

  renderRecaptchaOverlay () {
    if (!IS_STATUS_APP) {
      return null
    }

    return (
      <div className={ styles.recaptchaOverlay } ref={ (node) => this.overlayNode = node }>
        <span className={ styles.recaptchaPrivacyLink }>
          <Link to='https://policies.google.com/privacy'>Privacy</Link>
        </span>
        <span className={ styles.recaptchaTermsLink }>
          <Link to='https://policies.google.com/terms'>Terms</Link>
        </span>
      </div>
    )
  }

  componentDidMount () {
    const { placement } = this.props
    this.widgetID = window.grecaptcha.render(this.elementID, {
      sitekey: RECAPTCHA_KEY,
      size: 'invisible',
      badge: 'bottomright',
      callback: this.handleResponse,
    })
    this.props.registerRecaptchaWidget({
      placement,
      widgetID: this.widgetID,
      callback: this.handleResponse,
    })

    this.positionOverlay()
  }

  componentDidUpdate (prevProps: RecaptchaProps) {
    if (!prevProps.meta.invalid && this.props.meta.invalid) {
      captureWarning('Captcha verification failed')
      this.props.recaptchaFailed()
    }
  }

  componentWillUnmount () {
    this.onResponseDebounced.cancel()
    this.props.unregisterRecaptchaWidget()
    // I'm doing it here and not inside an epic because redux store gets cleared on user login
    // therefore the widget ID is available only here
    window.grecaptcha.reset(this.widgetID)
  }

  private positionOverlay () {
    if (!IS_STATUS_APP) {
      return
    }
    const recaptchaRootNode = document.getElementById(this.elementID)
    if (!recaptchaRootNode) {
      return
    }
    const recaptchaIframeNode = recaptchaRootNode.getElementsByTagName('iframe')[0]
    if (!recaptchaIframeNode) {
      return
    }
    recaptchaIframeNode.style.pointerEvents = 'none'

    const { overlayNode } = this
    if (!overlayNode) {
      return
    }
    const recaptchaWrapperNode = recaptchaRootNode.firstElementChild
    if (!recaptchaWrapperNode) {
      return
    }
    const bottom = getComputedStyle(recaptchaWrapperNode).bottom
    if (bottom) {
      overlayNode.style.bottom = bottom
    }
  }

  private handleResponse = (token: string) => {
    const { input: { onChange }} = this.props
    onChange(token)
    this.onResponseDebounced()
  }

  // delay is needed for redux-form to handle field value change
  private onResponseDebounced = debounce(() => {
    this.props.onResponse()
  })
}

const mapDispatchToProps = (dispatch: StoreDispatch, { placement }: RecaptchaOwnProps) => ({
  registerRecaptchaWidget: (payload: RegisterRecaptchaWidgetPayload) => dispatch(registerRecaptchaWidgetActionCreator(payload)),
  unregisterRecaptchaWidget: () => dispatch(unregisterRecaptchaWidgetActionCreator({ placement })),
  recaptchaFailed: () => dispatch(recaptchaFailedActionCreator({ placement })),
})

const recaptchaGuards: Guards = [
  recaptchaGuard,
]

const Recaptcha = connectLoadable(recaptchaGuards)()(RecaptchaComponent)

type RecaptchaStateProps = {
  recaptchaIsFailedToLoad: boolean
}

class RecaptchaWrapperComponent extends PureComponent<RecaptchaProps & RecaptchaStateProps> {
  render () {
    const { recaptchaIsFailedToLoad } = this.props
    const supportEmailChild = (
      <EmailLink email={ SUPPORT_EMAIL }>
        <span className={ styles.formGeneralErrorLink }>{ SUPPORT_EMAIL }</span>
      </EmailLink>
    )

    return recaptchaIsFailedToLoad
      ? (
        <div className={ styles.formGeneralError }>
          Your browser blocked the reCAPTCHA verification.
          Please <a href='' className={ styles.formGeneralErrorLink }>reload</a> the page and try again.
          If you continue to get this error, please contact us at: { supportEmailChild }
        </div>
      )
      : <Recaptcha { ...this.props } />
  }
}

const mapStateToProps = createSelector(
  recaptchaIsFailedToLoadSelector,
  (recaptchaIsFailedToLoad) => ({
    recaptchaIsFailedToLoad,
  })
)

const RecaptchaWrapper = connect<
  {}, RecaptchaDispatchProps, RecaptchaOwnProps
>(mapStateToProps, mapDispatchToProps)(RecaptchaWrapperComponent)


export default RecaptchaWrapper
